import { Editor, useEditorState } from "@tiptap/react";
import { useMemo, useCallback } from "react";
import { Box, HStack, Icon, VStack } from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import {
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaAlignJustify,
  FaUndo,
  FaRedo,
} from "react-icons/fa";
import { EditorButton } from "./EditorButton";

interface MenuBarProps {
  editor: Editor;
  showBlockSelector?: boolean;
  leftContent?: React.ReactNode;
}

export function MenuBar({
  editor,
  showBlockSelector = true,
  leftContent,
}: MenuBarProps) {
  // Read the current editor's state, and re-render the component when it changes
  const editorState = useEditorState({
    editor,
    selector: (ctx) => {
      return {
        isParagraph: ctx.editor.isActive("paragraph") ?? false,
        isHeading1: ctx.editor.isActive("heading", { level: 1 }) ?? false,
        isHeading2: ctx.editor.isActive("heading", { level: 2 }) ?? false,
        isHeading3: ctx.editor.isActive("heading", { level: 3 }) ?? false,
        isHeading4: ctx.editor.isActive("heading", { level: 4 }) ?? false,
        isHeading5: ctx.editor.isActive("heading", { level: 5 }) ?? false,
        isHeading6: ctx.editor.isActive("heading", { level: 6 }) ?? false,
        canUndo: ctx.editor.can().undo() ?? false,
        canRedo: ctx.editor.can().redo() ?? false,
        textAlignLeft: ctx.editor.isActive({ textAlign: "left" }) ?? false,
        textAlignCenter: ctx.editor.isActive({ textAlign: "center" }) ?? false,
        textAlignRight: ctx.editor.isActive({ textAlign: "right" }) ?? false,
        textAlignJustify:
          ctx.editor.isActive({ textAlign: "justify" }) ?? false,
        // Text-level styling detection
        isBold: ctx.editor.isActive("bold") ?? false,
        isItalic: ctx.editor.isActive("italic") ?? false,
        isStrike: ctx.editor.isActive("strike") ?? false,
        isCode: ctx.editor.isActive("code") ?? false,
        hasColor: ctx.editor.isActive("textStyle") ?? false,
        currentColor: ctx.editor.getAttributes("textStyle")?.color ?? null,
        // Check if there's a selection
        hasSelection: ctx.editor.state.selection.empty === false,
      };
    },
  });

  // Block formatting options for the select
  const blockFormatOptions = useMemo(
    () => [
      { value: "paragraph", label: "Paragraph" },
      { value: "heading1", label: "H1" },
      { value: "heading2", label: "H2" },
      { value: "heading3", label: "H3" },
    ],
    []
  );

  // Get current active block format only
  const getCurrentBlockFormat = () => {
    // Only show block-level formatting, never text-level styles
    if (editorState.isParagraph)
      return { value: "paragraph", label: "Paragraph" };
    if (editorState.isHeading1) return { value: "heading1", label: "H1" };
    if (editorState.isHeading2) return { value: "heading2", label: "H2" };
    if (editorState.isHeading3) return { value: "heading3", label: "H3" };
    return null;
  };

  // Helper function to get color name from hex value
  const getColorName = (color: string | null) => {
    if (!color) return "Text";

    const colorMap: Record<string, string> = {
      "#958DF1": "Purple",
      "#F98181": "Red",
      "#FBBC88": "Orange",
      "#FAF594": "Yellow",
      "#70CFF8": "Blue",
      "#94FADB": "Teal",
      "#B9F18D": "Green",
      "#FFB3D9": "Pink",
      "#9CA3AF": "Gray",
    };

    return colorMap[color] || "Colored Text";
  };

  // Handle block format change
  const handleBlockFormatChange = (selectedOption: any) => {
    if (!selectedOption) {
      // If nothing selected, don't do anything
      return;
    }

    switch (selectedOption.value) {
      case "paragraph":
        editor.chain().focus().setNode("paragraph").run();
        break;
      case "heading1":
        editor.chain().focus().setNode("heading", { level: 1 }).run();
        break;
      case "heading2":
        editor.chain().focus().setNode("heading", { level: 2 }).run();
        break;
      case "heading3":
        editor.chain().focus().setNode("heading", { level: 3 }).run();
        break;
      case "heading4":
        editor.chain().focus().setNode("heading", { level: 4 }).run();
        break;
      case "heading5":
        editor.chain().focus().setNode("heading", { level: 5 }).run();
        break;
      case "heading6":
        editor.chain().focus().setNode("heading", { level: 6 }).run();
        break;
    }
  };

  // Use only block format options
  const combinedOptions = useMemo(() => {
    return blockFormatOptions;
  }, [blockFormatOptions]);

  return (
    <Box
      className="control-group"
      // p={2}
      px={2}
      borderBottom={"1px solid"}
      borderColor={"gray.200"}
    >
      <VStack spacing={0} align="stretch">
        {/* Left content and text formatting buttons */}
        <HStack
          spacing={1}
          flexWrap="wrap"
          className="button-group"
          justify="space-between"
        >
          <HStack
            spacing={1}
            flexWrap="wrap"
            justifyContent="space-between"
            w="full"
          >
            <HStack
              justifyContent={"space-between"}
              position="relative"
              gap={4}
              flexGrow={1}
            >
              {leftContent}
              {/* Block formatting select */}
              {showBlockSelector && (
                <Box className="button-group" py={2} w="full">
                  <Select
                    options={combinedOptions}
                    value={getCurrentBlockFormat()}
                    onChange={handleBlockFormatChange}
                    placeholder="Format"
                    chakraStyles={{
                      dropdownIndicator: (provided, state) => ({
                        ...provided,
                        background: "none",
                        w: "50px",
                      }),
                      indicatorSeparator: (provided, state) => ({
                        ...provided,
                        display: "none",
                      }),
                      container: (provided, state) => ({
                        ...provided,

                        // w: "200px",
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? "blue.50" : "white",
                        color: "black",
                        cursor: "pointer",
                        textAlign: "left",
                        fontWeight: "normal",
                        fontStyle: "normal",
                      }),
                    }}
                  />
                </Box>
              )}
              <HStack gap={4}>
                {/* Text alignment buttons */}
                <HStack gap={0}>
                  <EditorButton
                    isActive={editorState.textAlignLeft}
                    onClick={() =>
                      editor.chain().focus().setTextAlign("left").run()
                    }
                  >
                    <Icon as={FaAlignLeft} />
                  </EditorButton>
                  <EditorButton
                    isActive={editorState.textAlignCenter}
                    onClick={() =>
                      editor.chain().focus().setTextAlign("center").run()
                    }
                  >
                    <Icon as={FaAlignCenter} />
                  </EditorButton>
                  <EditorButton
                    isActive={editorState.textAlignRight}
                    onClick={() =>
                      editor.chain().focus().setTextAlign("right").run()
                    }
                  >
                    <Icon as={FaAlignRight} />
                  </EditorButton>
                </HStack>

                <HStack gap={0}>
                  {/* Undo/Redo buttons */}
                  <EditorButton
                    onClick={() => editor.commands.undo()}
                    disabled={!editorState.canUndo}
                  >
                    <Icon as={FaUndo} />
                  </EditorButton>
                  <EditorButton
                    onClick={() => editor.commands.redo()}
                    disabled={!editorState.canRedo}
                  >
                    <Icon as={FaRedo} />
                  </EditorButton>
                </HStack>
              </HStack>
            </HStack>
          </HStack>
        </HStack>

        {/* History buttons */}
        <HStack spacing={2} flexWrap="wrap" className="button-group"></HStack>
      </VStack>
    </Box>
  );
}
